<template>
  <label
    class="check-button"
    :class="buttonClasses"
  >
    <input
      type="checkbox"
      :checked="!!modelValue"
      :disabled="disabled"
      @change="handleChange"
      @focus="focused = true"
      @blur="focused = false"
    />
    <span class="check-button__icon">
      <component :is="iconComponent" />
    </span>
    <span class="check-button__label">{{ label }}</span>
  </label>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useVModel } from '@vueuse/core';
  import { type IconComponent, IconName } from '../../types';

  const Icon: IconComponent = Object.fromEntries(
    Object.values(IconName).map((name) => [name, markRaw(defineAsyncComponent(() => import(`../icons/${name}.vue`)))]),
  ) as IconComponent;

  export interface CheckButtonProps {
    modelValue: boolean | null;
    label?: string;
    disabled?: boolean;
    isListItem?: boolean;
    iconName: IconName | null;
  }
  const props = withDefaults(defineProps<CheckButtonProps>(), {
    disabled: false,
    isListItem: false,
  });

  const emit = defineEmits<{
    'update:modelValue': [value: boolean | null];
  }>();

  const focused = ref(false);
  const innerValue = useVModel(props, 'modelValue', emit);

  const buttonClasses = computed(() => ({
    'check-button--checked': innerValue.value === true,
    'check-button--indeterminate': innerValue.value === null,
    'check-button--disabled': props.disabled,
    'check-button--list': props.isListItem,
  }));

  const handleChange = () => {
    if (innerValue.value === null) {
      innerValue.value = true;
    } else {
      innerValue.value = !innerValue.value;
    }
  };

  const iconComponent = computed(() => {
    if (props.iconName === null) {
      return null;
    }
    if (props.iconName in Icon) {
      return Icon[props.iconName as keyof IconComponent];
    }

    return Icon.Ufo;
  });
</script>
<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;

  .check-button {
    position: relative;
    display: inline-flex;
    gap: 8px;
    align-items: center;
    overflow: visible;
    box-sizing: border-box;
    padding: var(--comp-checkbutton-area-hovered, 8px);
    border-radius: var(--alias-radius-16, 16px);
    cursor: pointer;
    user-select: none;
  }

  .check-button--list {
    width: 100%;
    padding: var(--comp-checkbutton-area-hovered, 8px);
  }

  .check-button:not(.check-button--disabled):hover,
  .check-button:not(.check-button--disabled):active {
    background: var(--comp-checkbutton-color-area-hovered, rgba(0, 0, 0, 0.05));
  }

  .check-button:not(.check-button--disabled):active {
    background: var(--comp-checkbutton-color-area-active, rgba(0, 0, 0, 0.1));
  }

  .check-button:focus {
    background: var(--comp-checkbutton-color-area-focused, rgba(0, 0, 0, 0.1));
    box-shadow: 0 0 0 5px var(--alias-color-effects-focus, #fdf1d2);
    outline: 1px solid var(--comp-checkbutton-color-border-focused);
    outline-offset: var(--comp-checkbutton-area-focused);
  }

  .check-button--list:focus {
    background: var(--comp-checkbutton-color-area-focused, rgba(0, 0, 0, 0.1));
    box-shadow: none;
    outline: none;
  }

  .check-button input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
  }

  .check-button__icon {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    width: 32px;
    height: 32px;
    padding: 6px;
    border-radius: var(--alias-radius-8, 8px);
    background: var(--comp-checkbutton-color-background, rgba(0, 0, 0, 0.05));
  }

  .check-button--checked .check-button__icon:after {
    content: '';
    position: absolute;
    top: -6px;
    right: -6px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: white;
    background-image: url('check.svg');
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
  }

  .check-button--checked:not(.check-button--disabled):focus .check-button__icon:after {
    background-color: #e5e5e5;
  }

  .check-button--checked:not(.check-button--disabled):hover .check-button__icon:after {
    background-color: #f2f2f2;
  }

  .check-button--checked:not(.check-button--disabled):active .check-button__icon:after {
    background-color: #e5e5e5;
  }

  .check-button__icon :deep(svg) {
    width: 100%;
    height: 100%;
  }

  .check-button__icon :deep(path) {
    fill: var(--comp-checkbutton-color-icon);
  }

  .check-button__label {
    display: flex;
    align-items: center;
    height: 32px;
    color: var(--comp-checkbutton-color-text);
    text-wrap: nowrap;

    @include typography.base-400-400-regular;
  }

  .check-button--checked .check-button__icon {
    background-color: var(--comp-checkbutton-color-background-selected);
  }

  .check-button:not(.check-button--disabled, .check-button--checked):hover .check-button__icon,
  .check-button:not(.check-button--disabled, .check-button--checked):active .check-button__icon,
  .check-button:not(.check-button--disabled, .check-button--checked):focus-within .check-button__icon {
    background: var(--comp-checkbutton-color-background-hovered, #fff);
  }

  .check-button--checked .check-button__icon :deep(path),
  .check-button--indeterminate .check-button__icon :deep(path) {
    fill: var(--comp-checkbutton-color-icon-selected);
  }

  .check-button--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .check-button--disabled .check-button__label {
    color: var(--comp-checkbutton-color-text-disabled);
  }

  .check-button--disabled .check-button__icon :deep(path) {
    fill: var(--comp-checkbutton-color-icon-disabled);
  }

  .check-button--disabled.check-button--checked .check-button__icon :deep(path),
  .check-button--disabled.check-button--indeterminate .check-button__icon :deep(path) {
    fill: var(--comp-checkbutton-color-background-selected-disabled);
  }
</style>
